import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { IReportingDetail } from 'src/app/interfaces/ireporting-detail';
import { LearnerListModalComponent } from '../learner-list-modal/learner-list-modal.component';
import { Router } from '@angular/router';
import { MAT_MENU_DEFAULT_OPTIONS } from '@angular/material/menu';
import { TooltipService } from 'src/app/services/general/tooltip.service';
import { IModuleAssignmentDetail } from 'src/app/interfaces/imodule-assignment-detail';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { TranslocoService } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';

@Component({
  selector: 'app-certification-assignments',
  templateUrl: './certification-assignments.component.html',
  styleUrls: ['./certification-assignments.component.scss'],
  standalone: false,
  providers: [
    {
      provide: MAT_MENU_DEFAULT_OPTIONS,
      useValue: { overlayPanelClass: 'custom-certification-assignments-menu-overlay-pane' },
    },
  ],
})

export class CertificationAssignmentsComponent {
  @Output() loadCertificationAssignments: EventEmitter<void> = new EventEmitter();
  @Input() liveData: IReportingDetail | undefined;
  @Input() oldData: IReportingDetail | undefined;
  @Input() headerText: string = '';
  @Input() summaryText: string = '';
  @Input() moduleAssignmentCount: number | undefined;
  reportData: IReportingDetail | undefined;

  header: string = '';
  summary: string = '';
  content: string = '';
  showOldAssignments: boolean = true;
  ctaText: string = '';
  ctaImg = "/assets/icons/arrow/chevron-down-dark-blue.svg"
  toggleString: string = '';
  remainingAssignments: number = 0;
  oldDataLoading: boolean = false;
  sortField: string = "dueDate";
  sortAscending: boolean = false;
  sortIcon: string = "/assets/icons/arrow/arrow-narrow-up.svg";
  translationLoaded: boolean = false;

  constructor(private dialog: MatDialog,
    private router: Router,
    private tooltipService: TooltipService,
    private amplitudeService: AmplitudeService,
    private translocoService: TranslocoService,
    private translationService: TranslationService

  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['oldData']) {
      this.oldDataLoading = false;
      this.oldData?.moduleAssignmentsDetail.forEach(assignment => {
        assignment.oldData = true;
        assignment.visible = true;
      });

      if (this.liveData && this.oldData && this.liveData.moduleAssignmentsDetail && this.oldData.moduleAssignmentsDetail) {
        // Combine the moduleAssignmentsDetail arrays from both liveData and oldData
        const combinedAssignments = [
          ...this.liveData.moduleAssignmentsDetail,
          ...this.oldData.moduleAssignmentsDetail
        ];
        this.reportData = {
          ...this.liveData,
          moduleAssignmentsDetail: combinedAssignments
        };
      }

    }

  }

  ngOnInit() {
    this.translationService.loadScopedTranslation('reporting').then(() => {
      this.ctaText = this.translocoService.translate('SEE_CERTIFICATION_ASSIGNMENT', {}, 'reporting');
      this.reportData = this.liveData;
      this.reportData?.moduleAssignmentsDetail.forEach(assignment => {
        assignment.visible = true;
        assignment.oldData = false;
      });
      this.toggleOldAssignments();
      this.setSortField(this.sortField);
      this.translationLoaded = true;
    })
    
  }

  isExpired(detail: any) {
    return detail.daysLeft <= 0;
  }

  getPositiveNumber(value: any): number {
    return Math.abs(value.daysLeft);
  }

  openModal(moduleAssignmentUuid: string, category: string, count: number, percentage: number, moduleAssignmentName: string): void {
    // Sending the Event of Clicking on a cell to access the User List to Amplitude
    this.amplitudeService.trackEvent('rollupreport_click_userlist', {
      'learner_list_status': category,
    });

    const selectedAccountInfo = localStorage.getItem('selected_account_info');
    const currentAccountUuid = localStorage.getItem('current_account_uuid');
    const accountUuid = selectedAccountInfo ? JSON.parse(selectedAccountInfo).uuid : currentAccountUuid;

    this.dialog.open(LearnerListModalComponent, {
      hasBackdrop: true,
      data: {
        accountUuid: accountUuid,
        moduleAssignmentUuid: moduleAssignmentUuid,
        category: category,
        moduleAssignmentType: 'CERTIFICATION',
        count: count,
        percentage: percentage,
        moduleAssignmentName: moduleAssignmentName
      },
    });
  }

  toggleOldAssignments() {
    if (this.oldDataLoading) {
      return;
    }
    this.showOldAssignments = !this.showOldAssignments;
    if (!this.showOldAssignments) {
      this.ctaText = this.translocoService.translate('SEE_CERTIFICATION_ASSIGNMENT', {}, 'reporting');
      this.ctaImg = "assets/icons/arrow/chevron-up-dark-blue.svg";
    } else {
      this.ctaText = this.translocoService.translate('HIDE_OLDER_ASSIGNMENTS', {}, 'reporting');
      this.ctaImg = "/assets/icons/arrow/chevron-down-dark-blue.svg";
    }
    this.reportData?.moduleAssignmentsDetail.forEach(assignment => {
      if (assignment.oldData) {
        assignment.visible = this.showOldAssignments;
      }
    });

    this.remainingAssignments = (this.moduleAssignmentCount ?? 0) - (this.liveData?.moduleAssignmentsDetail?.length ?? 0);
    if (!this.showOldAssignments && this.remainingAssignments > 0) {
      this.toggleString = `${this.remainingAssignments} ${this.translocoService.translate('OLDER_CERTIFICATION', {}, 'reporting')} ${this.remainingAssignments === 1 ? `${this.translocoService.translate('ASSIGNMENT_IS', {}, 'reporting')}` : `${this.translocoService.translate('ASSIGNMENT_ARE', {}, 'reporting')}`} ${this.translocoService.translate('CURRENTLY_HIDDEN', {}, 'reporting')}.`;
    } else {
      this.toggleString = this.translocoService.translate('ALL_CERTIFICATION_ASSIGNMENTS', {}, 'reporting');
    }
  }

  fetchOldAssignments() {
    // Sending the Event of Clicking 'See all certification assignments' to Amplitude
    this.amplitudeService.trackEvent('rollupreport_click_expandcertificationassignments');

    if (!this.oldData) {
      this.oldDataLoading = true;
      this.loadCertificationAssignments.emit();
    }

  }

  sortAssignments() {
    this.reportData?.moduleAssignmentsDetail.sort((a, b) => {
      const dateA = new Date(a.dueDate).getTime();
      const dateB = new Date(b.dueDate).getTime();
      return this.sortAscending ? dateA - dateB : dateB - dateA;
    });
  }

  setSortField(sortField: string) {
    this.sortField = sortField;
    this.toggleSortOrder();
  }

  toggleSortOrder() {
    this.sortAscending = !this.sortAscending;
    if (this.sortAscending) {
      this.sortIcon = '/assets/icons/arrow/arrow-narrow-down.svg';
    } else {
      this.sortIcon = '/assets/icons/arrow/arrow-narrow-up.svg';
    }
    this.sortList();
  }

  sortList() {
    console.log("calling sort list", this.sortField);
    if (!this.reportData?.moduleAssignmentsDetail || !this.sortField) return;

    const sortOrder = this.sortAscending;
    const sortKey = this.sortField;

    const getNestedValue = (obj: any, key: string) => key.split('.').reduce((o, k) => (o ? o[k] : undefined), obj);
    this.reportData?.moduleAssignmentsDetail.sort((a, b) => {
      const aValue = getNestedValue(a, sortKey);
      const bValue = getNestedValue(b, sortKey);

      if (aValue === undefined || bValue === undefined) {
        return 0;
      }

      if (sortKey === 'dueDate') {
        return sortOrder
          ? new Date(aValue).getTime() - new Date(bValue).getTime()
          : new Date(bValue).getTime() - new Date(aValue).getTime();
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder
          ? aValue - bValue
          : bValue - aValue;
      } else {
        return 0;
      }
    });
  }

  navigateToRoute(module: IModuleAssignmentDetail) {
    // Sending the Clicked Module Name and UUID from Module Link (in Module Column) to Amplitude
    this.amplitudeService.trackEvent('rollupreport_click_modulelink', {
      'module_name': module.moduleName,
      'module_uuid': module.moduleUuid,
    });
    const returnUrl = `/reporting_visualization?moduleUuid=${module.moduleUuid}`;
    localStorage.setItem('returnUrl', encodeURIComponent(returnUrl))

    this.router.navigate(['/reporting_visualization'], { queryParams: { moduleUuid: module.moduleUuid } });
  }

  openTooltip(event: MouseEvent, direction: string, content: string, width: number, height: number): void {
    let heading = '';
    let paragraph = '';
    if (content === 'notStarted') {
      paragraph = this.translocoService.translate('CERTIFICATION_NOT_STARTED_TOOLTIP', {}, 'reporting');
    } else if (content === 'inProgress') {
      paragraph = this.translocoService.translate('CERTIFICATION_IN_PROGRESS_TOOLTIP', {}, 'reporting');
    } else if (content === 'certified') {
      paragraph = this.translocoService.translate('CERTIFICATION_CERTIFIED_TOOLTIP', {}, 'reporting');
    } else if (content === 'userAssignmentCount') {
      paragraph = this.translocoService.translate('CERTIFICATION_USER_ASSIGNMENT_TOOLTIP', {}, 'reporting');
    }
    this.tooltipService.openTooltip(event, direction, heading, paragraph, width, height);
  }
}

